import { Controller } from 'stimulus'
import I18n from '../../../javascript/i18n/index.js.erb'

/**
 * @namespace Actions Flyout
 */
export default class extends Controller {
  static targets = ['flyout', 'content', 'heading', 'loadingIndicator']

  /**
   * Edit an Action in the Flyout's main content zone.
   * If there's an error in the process, let the user know.
   * @param event
   * @returns {Promise<string | string>}
   */
  edit(event) {
    const actionId = event.target.dataset.actionId
    const afterActionUrl = event.target.dataset.afterActionUrl

    this.fetchAction(`/actions/${actionId}/edit?flyout=true`, afterActionUrl)
      .then(() => {
        this.headingTarget.innerHTML = `<h1>${I18n.t('actions.update.heading')}</h1>`
      })
      .catch(() => {
        this.contentTarget.innerHTML = `<h2 class='flyout__error'>${I18n.t('actions.flyout.edit_failed')}</h2>`
      })
  }

  /**
   * Load an Action's details into the Flyout's main content zone.
   * If there's an error in the process, let the user know.
   * @param actionId
   * @param afterActionUrl
   * @returns {Promise<string | string>}
   */
  load(actionId, afterActionUrl = null) {
    return this.fetchAction(`/actions/${actionId}?flyout=true`, afterActionUrl).then(() => {
      // Let assistive technologies know that the area is no longer loading
      $(this.flyoutTarget).attr('aria-busy', 'false')
      $(this.loadingIndicatorTarget).attr('hidden', '')
    }).catch(() => {
      this.contentTarget.innerHTML = `<h2 class='flyout__error'>${I18n.t('actions.flyout.load_failed')}</h2>`
    })
  }

  /**
   * Load an action into flyout based on event data.
   * @param event
   * @returns {Promise<string | string>}
   */
  loadEventAction(event) {
    this.reset()

    return this.load(event.target.dataset.actionId, event.target.dataset.afterActionUrl)
  }

  ajaxSuccess(event) {
    this.loadEventAction(event)
      .then(() => window.dispatchEvent(new CustomEvent('action-updated')))
  }

  ajaxError(event) {
    const [, , xhr] = event.detail
    this.contentTarget.innerHTML = xhr.response
  }

  /**
   * Handle Action list item button click.
   * TODO get desired Action ID via Stimulus (which doesn't seem doable in this case)
   * {@link https://github.com/hotwired/stimulus/issues/64#issuecomment-369290931}
   * @param event
   */
  triggerFlyout(event) {
    const actionId = event.currentTarget.dataset.actionId
    const afterActionUrl = event.currentTarget.dataset.afterActionUrl
    const isInFlyout = event.currentTarget.dataset.flyout

    if (isInFlyout === 'true') {
      window.location = `/actions/${actionId}`
      return
    }

    if (actionId && this.flyoutTarget.dataset.actionId !== actionId) {
      // Clear out the current Flyout content, and show the loading indicator (again)
      this.reset()
      // Capture the current Action ID in a data attribute on the Flyout itself
      this.flyoutTarget.dataset.actionId = actionId
      // Let assistive technologies know that the area is loading
      $(this.flyoutTarget).attr('aria-busy', 'true')
      //
      this.load(actionId, afterActionUrl)
    }
  }

  fetchAction(url, afterActionUrl = null) {
    if (afterActionUrl) {
      url += `&after_action_url=${afterActionUrl}`
    }

    url += `&read_only=${this.flyoutTarget.dataset.readOnly}`

    return fetch(url)
      .then(response => response.text())
      .then((html) => {
        this.contentTarget.innerHTML = html
      })
  }

  reset() {
    $(this.contentTarget).empty()
    $(this.headingTarget).empty()
    $(this.loadingIndicatorTarget).removeAttr('hidden')
  }
}
